// ============================================================================
// Cotizaciones (#1545) — las mismas cuentas que el servidor
// (apps/torre-de-control-api/functions/src/services/quoteMath.js), para los
// totales en vivo del armador. El servidor recalcula al guardar: esto solo
// muestra. Si cambias una regla, cámbiala en los dos lados.
//
//   • Renglón: base = cantidad × precio; importe = base − descuento del renglón.
//   • Descuento global sobre la suma de importes, repartido para el IVA.
//   • IVA por renglón (fracción, 0.16 por omisión); redondeo a 2 decimales.
//   • Precio vacío = «por cotizar»: suma 0 y la cotización no se puede enviar.
// ============================================================================

export const DEFAULT_TAX_RATE = 0.16;

export interface QuoteItem {
  productId?: string | null;
  source?: string | null;
  sku?: string | null;
  name: string;
  description?: string | null;
  quantity: number;
  /** null = por cotizar. */
  unitPrice: number | null;
  discountPct?: number;
  /** Fracción (0.16). */
  taxRate?: number | null;
  amount?: number;
  image?: string | null;
}

export interface QuoteTotals {
  items: (QuoteItem & { amount: number })[];
  subtotal: number;
  discountTotal: number;
  taxTotal: number;
  total: number;
  hasUnpriced: boolean;
}

export function round2(value: number): number {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

/** 16 → 0.16; 0.16 → 0.16; vacío → 0.16. */
export function taxRateOf(v: number | null | undefined): number {
  if (v === null || v === undefined || !Number.isFinite(Number(v))) return DEFAULT_TAX_RATE;
  const n = Number(v);
  return Math.round((n > 1 ? n / 100 : n) * 10000) / 10000;
}

const isUnpriced = (item: QuoteItem) => item.unitPrice === null || item.unitPrice === undefined || !Number.isFinite(Number(item.unitPrice));

export function lineAmount(item: QuoteItem): number {
  if (isUnpriced(item)) return 0;
  const base = round2(Number(item.quantity) * Number(item.unitPrice));
  return round2(base - round2((base * (Number(item.discountPct) || 0)) / 100));
}

export function computeQuote(items: QuoteItem[], globalDiscountPct = 0): QuoteTotals {
  const g = round2(Math.min(100, Math.max(0, Number(globalDiscountPct) || 0)));
  const rows = items.map((it) => ({ ...it, amount: lineAmount(it) }));
  let subtotal = 0;
  let lineDiscounts = 0;
  let net = 0;
  for (const it of rows) {
    if (isUnpriced(it)) continue;
    const base = round2(Number(it.quantity) * Number(it.unitPrice));
    subtotal = round2(subtotal + base);
    lineDiscounts = round2(lineDiscounts + round2(base - it.amount));
    net = round2(net + it.amount);
  }
  const globalDiscount = round2((net * g) / 100);
  const factor = net > 0 ? (net - globalDiscount) / net : 0;
  let taxTotal = 0;
  for (const it of rows) {
    if (isUnpriced(it)) continue;
    taxTotal = round2(taxTotal + round2(it.amount * factor * taxRateOf(it.taxRate)));
  }
  const discountTotal = round2(lineDiscounts + globalDiscount);
  return {
    items: rows,
    subtotal,
    discountTotal,
    taxTotal,
    total: round2(subtotal - discountTotal + taxTotal),
    hasUnpriced: rows.some(isUnpriced),
  };
}

/**
 * Lo que impide ENVIAR (mismas reglas que el servidor): sin renglones, con
 * renglones por cotizar, cantidades o precios inválidos, o vigencia pasada.
 */
export function sendBlockers(items: QuoteItem[], expiresAt: string | null, now = Date.now()): string[] {
  const out: string[] = [];
  if (!items.length) out.push("Agrega al menos un renglón.");
  if (items.some((it) => !String(it.name || "").trim())) out.push("Cada renglón necesita nombre.");
  if (items.some((it) => !(Number(it.quantity) > 0))) out.push("Las cantidades deben ser mayores que 0.");
  if (items.some(isUnpriced)) out.push("Hay renglones sin precio (por cotizar).");
  if (items.some((it) => !isUnpriced(it) && Number(it.unitPrice) < 0)) out.push("Los precios no pueden ser negativos.");
  if (items.some((it) => Number(it.discountPct) < 0 || Number(it.discountPct) > 100)) out.push("El descuento va de 0 a 100 %.");
  const until = expiresAt ? Date.parse(expiresAt) : NaN;
  if (Number.isFinite(until) && until < now) out.push("La vigencia ya pasó: cambia «Válida hasta».");
  return out;
}
